import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BILLING_CYCLES,
  BILLING_CYCLE_LABELS,
  CYCLE_UNIT,
  addOnPriceIdForCycle,
  planPriceIdForCycle,
  type BillingCycle,
} from "@/config/stripePlans";
import { AiAssistantIcon, BookingsModeIcon } from "../icons/WizardModeIcons";
import TrialLengthFields from "../TrialLengthFields";
import {
  plans,
  addOns,
  computeTotal,
  computeChargePerCycle,
  bookingsCompatible,
  clientUsesBookings,
  isEarlySupporter,
  isTrialStart,
  EARLY_SUPPORTER_GRANTS,
} from "../pricing";
import type { StartMode, StepProps } from "../types";

const PlanAddOnsStep = ({ data, update }: StepProps) => {
  const cycle = data.billingCycle;
  const total = computeTotal(data);
  const chargePerCycle = computeChargePerCycle(data);
  const canPickBookings = bookingsCompatible(data);
  const earlySupporter = isEarlySupporter(data);
  const usesBookings = clientUsesBookings(data);
  const trialStart = isTrialStart(data);

  // The Early Supporter plan is a hidden, founder-only comp. It is not rendered
  // until unlocked with Ctrl/Cmd+Shift+E so staff (or anyone screen-sharing)
  // can't hand it out. Stays visible once it's been selected this session.
  const [esRevealed, setEsRevealed] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "e") {
        e.preventDefault();
        setEsRevealed(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // A plan without a Stripe price on the new cycle (Enterprise is not sold
  // quarterly) can't stay selected: fall back to the default plan.
  const changeCycle = (next: BillingCycle) => {
    const selected = plans[data.selectedPlan];
    const stillSold = !selected?.stripe || planPriceIdForCycle(selected, next) !== null;
    update({ billingCycle: next, ...(stillSold ? {} : { selectedPlan: "growing" }) });
  };

  const toggleAddOn = (key: string, on: boolean) => {
    update({
      selectedAddOns: on
        ? [...data.selectedAddOns, key]
        : data.selectedAddOns.filter((k) => k !== key),
    });
  };

  const toggleGrant = (key: string, on: boolean) => {
    update({
      earlySupporterGrants: on
        ? [...data.earlySupporterGrants, key]
        : data.earlySupporterGrants.filter((k) => k !== key),
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-heading font-semibold">Plan & add-ons</h2>
        <p className="text-sm text-muted-foreground">
          {trialStart
            ? "Choose the plan and any add-ons they get during the trial."
            : "Choose the plan and any add-ons. The total below is what the client pays via their payment link."}
        </p>
      </div>

      {!earlySupporter && (
        <div className="space-y-3">
          <Label>How will they start?</Label>
          <Tabs value={data.startMode} onValueChange={(v) => update({ startMode: v as StartMode })}>
            <TabsList aria-label="How the client starts">
              <TabsTrigger value="payment">Payment link</TabsTrigger>
              <TabsTrigger value="trial">Trial</TabsTrigger>
            </TabsList>
          </Tabs>
          {trialStart && (
            <div className="rounded-lg border p-3">
              <TrialLengthFields
                idPrefix="wizard-trial"
                days={data.trialDays}
                neverExpires={data.trialNeverExpires}
                onChange={(patch) =>
                  update({
                    ...(patch.days !== undefined ? { trialDays: patch.days } : {}),
                    ...(patch.neverExpires !== undefined ? { trialNeverExpires: patch.neverExpires } : {}),
                  })
                }
              />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Tabs value={cycle} onValueChange={(v) => changeCycle(v as BillingCycle)}>
          <TabsList aria-label="Billing cycle">
            {BILLING_CYCLES.map((c) => (
              <TabsTrigger key={c} value={c}>
                {BILLING_CYCLE_LABELS[c]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(plans).map(([key, plan]) => {
          const selected = data.selectedPlan === key;
          // "free" is the internal cancelled/no-sub floor, not a sellable tier.
          // Never offer it in onboarding. Sellable plans start at Starter.
          if (key === "free") {
            return null;
          }
          if (key === "early_supporter" && !esRevealed && !selected) {
            return null;
          }
          const soldOnCycle = !plan.stripe || planPriceIdForCycle(plan, cycle) !== null;
          return (
            <button
              key={key}
              type="button"
              disabled={!soldOnCycle}
              onClick={() => update({ selectedPlan: key })}
              className={cn(
                "text-left rounded-xl border p-4 transition-all",
                !soldOnCycle && "cursor-not-allowed opacity-50",
                selected
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "border-border hover:border-primary/40"
              )}
            >
              <div className="flex items-baseline justify-between">
                <span className="font-heading font-semibold">{plan.name}</span>
                {key === "enterprise" ? (
                  <span className="text-sm font-semibold text-muted-foreground">
                    Custom
                  </span>
                ) : key === "early_supporter" ? (
                  <span className="text-sm font-semibold text-primary">Free</span>
                ) : (
                  <span className="text-sm font-semibold">
                    ${plan.price[cycle] ?? plan.price.monthly}
                    <span className="text-muted-foreground font-normal">/mo</span>
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {soldOnCycle ? plan.description : `Not sold on ${cycle} billing`}
              </p>
              {/* Usage-pricing transparency: contacts, overage, and postpaid
                  broadcast rate so the owner sees what they'll be billed for
                  beyond the flat fee. Sourced from stripePlans limits — no
                  hardcoded figures. Hidden where not applicable (Free/Enterprise). */}
              {key !== "early_supporter" && (
                <div className="mt-2 space-y-0.5 border-t border-border/50 pt-2 text-[11px] text-muted-foreground">
                  {plan.limits.mac_base != null && (
                    <div>
                      {plan.limits.mac_base.toLocaleString()} engaged customers/mo
                      {plan.limits.mac_ceiling != null &&
                        plan.limits.mac_ceiling !== plan.limits.mac_base &&
                        ` (up to ${plan.limits.mac_ceiling.toLocaleString()})`}
                    </div>
                  )}
                  {plan.limits.mac_overage_pack_size != null &&
                    plan.limits.mac_overage_pack_price_usd != null && (
                      <div>
                        Extra engaged customers: ${plan.limits.mac_overage_pack_price_usd}/
                        {plan.limits.mac_overage_pack_size}-pack
                      </div>
                    )}
                  {plan.limits.broadcast_rate_per_message != null && (
                    <div>
                      Broadcasts: ${plan.limits.broadcast_rate_per_message.toFixed(2)}/message
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        <Label>Add-ons</Label>
        <div className="space-y-2">
          {Object.values(addOns).map((a) => {
            const checked = data.selectedAddOns.includes(a.key);
            const disabled = a.key === "bookings" && !canPickBookings;
            // On Early Supporter, Bookings is granted free via the comped grant
            // below. Don't also show the paid Bookings add-on.
            if (earlySupporter && a.key === "bookings") {
              return null;
            }
            return (
              <label
                key={a.key}
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-3 transition-colors",
                  disabled
                    ? "cursor-not-allowed opacity-50 border-border"
                    : "cursor-pointer",
                  checked && !disabled ? "border-primary bg-primary/5" : "border-border"
                )}
              >
                <Checkbox
                  checked={checked && !disabled}
                  disabled={disabled}
                  onCheckedChange={(v) => toggleAddOn(a.key, v === true)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{a.name}</span>
                    <span className="text-sm">
                      +${a.price.monthly}/mo
                      {cycle === "quarterly" && addOnPriceIdForCycle(a, cycle) && (
                        <span className="text-muted-foreground"> (billed quarterly)</span>
                      )}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {a.description}
                  </p>
                  {disabled && (
                    <p className="text-xs text-amber-600 mt-1">
                      Bookings is billed monthly or quarterly. Switch the plan
                      to monthly or quarterly billing to add it.
                    </p>
                  )}
                </div>
              </label>
            );
          })}

          {/* Early Supporter comped grants — only shown when that plan is
              selected. Each is independently selectable and always free. */}
          {earlySupporter &&
            EARLY_SUPPORTER_GRANTS.map((g) => {
              const checked = data.earlySupporterGrants.includes(g.key);
              return (
                <label
                  key={g.key}
                  className={cn(
                    "flex items-start gap-3 rounded-lg border p-3 transition-colors cursor-pointer",
                    checked ? "border-primary bg-primary/5" : "border-border"
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => toggleGrant(g.key, v === true)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{g.name}</span>
                      <span className="text-sm font-medium text-primary">Free</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{g.description}</p>
                  </div>
                </label>
              );
            })}
        </div>
      </div>

      {/* Mode callout — the booking-vs-AI-only fork made explicit. Driven purely
          by whether Bookings is enabled, so the operator always sees which mode
          the client will land in. */}
      <div
        className={cn(
          "flex items-start gap-3 rounded-xl border p-4",
          usesBookings ? "border-purple-200 bg-purple-50/60" : "border-amber-200 bg-amber-50/60"
        )}
      >
        <div
          className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
            usesBookings ? "bg-purple-100 text-purple-600" : "bg-amber-100 text-amber-600"
          )}
        >
          {usesBookings ? <BookingsModeIcon className="w-5 h-5" /> : <AiAssistantIcon className="w-5 h-5" />}
        </div>
        <div className="text-sm">
          <div className="font-semibold">
            {usesBookings ? "Dibs Bookings client" : "AI-assistant-only client"}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {usesBookings
              ? "The AI takes bookings and payments through Dibs. Set up their listings in the dashboard after onboarding."
              : "No Dibs Bookings — the AI answers and takes actions using the client's own data and systems. After onboarding, connect their data sources under Business data → Connections."}
          </p>
        </div>
      </div>

      {trialStart ? (
        <div className="rounded-xl bg-secondary p-4 text-sm font-medium">
          No payment during the trial
        </div>
      ) : (
        <div className="flex items-center justify-between rounded-xl bg-secondary p-4">
          <span className="font-medium">Total</span>
          <span className="text-right">
            <span className="text-xl font-heading font-bold">
              ${total}
              <span className="text-sm text-muted-foreground font-normal">/mo</span>
            </span>
            {cycle !== "monthly" && data.selectedPlan !== "free" && (
              <span className="block text-xs text-muted-foreground">
                ${chargePerCycle.toLocaleString()}/{CYCLE_UNIT[cycle]}, billed {cycle}
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default PlanAddOnsStep;
